import { useState, useEffect, useRef } from 'react';
import { getEmployeeDetailsByLogin } from "src/Ateam-BI/Templates/Utils/AppActivityUtils";

// Job family codes are exact short codes — use a direct map for O(1) lookup
const JOB_FAMILY_TO_LABEL = {
  'DATENG': 'Data Engineer',
  'PROSRV': 'Professional Services II',
  'ACCTG': 'Accountant',
  'FINAC': 'Accountant',
  'SFTDE': 'Software Engineer',
};

// Title keywords use word-boundary matching to avoid false positives
// e.g. 'LEAD SOFTWARE ENGINEER' should match 'SOFTWARE ENGINEER' but not 'SOFTWARE SALES MANAGER'
const TITLE_PATTERNS = [
  { pattern: /\bDATA ENGINEER\b/, label: 'Data Engineer' },
  { pattern: /\bPROFESSIONAL SERVICES\b/, label: 'Professional Services II' },
  { pattern: /\bACCOUNTANT\b/, label: 'Accountant' },
  { pattern: /\bSOFTWARE DEVELOPMENT ENGINEER\b|\bSOFTWARE ENGINEER\b/, label: 'Software Engineer' },
];

export const resolvePersona = (jobFamily, title) => {
  const jf = (jobFamily || '').toUpperCase().trim();
  const t = (title || '').toUpperCase();

  // Job family exact match takes priority
  if (jf && JOB_FAMILY_TO_LABEL[jf]) return JOB_FAMILY_TO_LABEL[jf];

  // Fall back to word-boundary title matching
  const titleMatch = TITLE_PATTERNS.find(({ pattern }) => pattern.test(t));
  return titleMatch ? titleMatch.label : 'General';
};

export const usePersona = (papiAlias, firstName, enabled = true) => {
  const [personaText, setPersonaText] = useState(
    (enabled && papiAlias) ? 'Loading persona...' : 'For persona based conversations, change the source in the settings.'
  );
  const [userName, setUserName] = useState(firstName);
  const [hasApiName, setHasApiName] = useState(false);
  const firstNameRef = useRef(firstName);
  firstNameRef.current = firstName;

  // Keep userName in sync with firstName when no API-derived name is available
  useEffect(() => {
    if (!hasApiName) {
      setUserName(firstName);
    }
  }, [firstName, hasApiName]);

  useEffect(() => {
    if (!enabled || !papiAlias) {
      setHasApiName(false);
      setUserName(firstNameRef.current);
      setPersonaText('For persona based conversations, change the source in the settings.');
      return;
    }
    setPersonaText('Loading persona...');
    setHasApiName(false);
    setUserName(firstNameRef.current);
    let cancelled = false;
    const fetchPersona = async () => {
      try {
        const result = await getEmployeeDetailsByLogin(papiAlias);
        if (cancelled) return;
        const raw = result?.data?.getEmployeeDetailsByLogin;
        if (!raw) throw new Error('Empty employee details response');
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const apiFirstName = data?.basicInfo?.firstName;
        if (apiFirstName) {
          setHasApiName(true);
          setUserName(apiFirstName);
        } else {
          setHasApiName(false);
          setUserName(firstNameRef.current);
        }
        const jf = data?.job?.jobFamilyName || data?.jobFamilyName || '';
        const title = data?.basicInfo?.businessTitle || data?.job?.businessTitle || '';
        setPersonaText(`Selected Persona is - ${resolvePersona(jf, title)}`);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch persona:', error);
        setHasApiName(false);
        setUserName(firstNameRef.current);
        setPersonaText('For persona based conversations, change the source in the settings.');
      }
    };
    fetchPersona();
    return () => { cancelled = true; };
  }, [papiAlias, enabled]);

  return { personaText, userName };
};
